import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, Container, Grid, InputAdornment } from '@mui/material';
import { Popper, TextField, Typography, useMediaQuery } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { styled, useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import React from "react";
import getPageMetaTags from "../../lib/meta/_getMetaTags";
import PropTypes from 'prop-types';
import { VariableSizeList } from 'react-window';
import useAuth from "../../src/hook/auth";
import {
  Slide,
  useScrollTrigger
} from '@mui/material';
import matter from "gray-matter";
import useSWR from "swr";
import * as send from '../../lib/analytics/send';
import getStockIdeas from "../../lib/stock-ideas/_getStockIdeas";
import getLatestFilings from "../../lib/stock/_getLatestFilings";
import getStockTabs from "../../lib/stock/_getStockTabsData";
import getStocksHeatMapData from "../../lib/stock/_getStocksHeatMapData";
import dynamic from 'next/dynamic';
import { getHeroContent, searchTicker, sortByDate } from "../../src/utils/helpers";
import { convertDate, getTradingItem } from "../../src/utils/time";
import _getLatestPrice from "../api/_getLatestPrice";
import _getTickerInfo from "../api/_getTickerInfo";
import { colors, radii, shadows } from '../../src/utils/designTokens';
const TopMetricsWithMarquee = dynamic(() => import('../../src/components/Stock/TopMetrics/marquee'))
const TodaysWinnersLosers = dynamic(() => import('../../src/components/Stock/TodaysWinnersLosers'))
const StockIdeasAdvertisement = dynamic(() => import('../../src/components/StockIdeasAdvertisement'))
const StocksHeatMap = dynamic(() => import('../../src/components/Stock/StocksHeatMap'))
const NewStock = dynamic(() => import('../../src/components/Stock'))

const convertData = (data) => {
  const result = data.reduce((acc, { ticker, r, p }) => {
    if (!acc[ticker]) {
      acc[ticker] = {
        ticker,
        price_data: [],
      };
    }
    acc[ticker].price_data.push({ price: p });
    return acc;
  }, {});
  return result;
}

export const getPercent = (a, b) => {
  const percent = roundNum((a - b) * 100 / b, 2);
  return percent >= 0 ? `+${percent}` : percent;
}

export const roundNum = (num) => {
  const n = num;
  if (!n || isNaN(n)) return n;
  return (Math.round(n * Math.pow(10, 2)) / Math.pow(10, 2)).toFixed(2);
};

const useMergedData = (heatmapData) => {
  const { data, error, isValidating } = useSWR(["api/_getLatestPrice", heatmapData.map(d => d.ticker)], _getLatestPrice, {
    refreshInterval: 420000,
    revalidateOnFocus: false,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (error.status === 404) return
      if (key === 'api/_getLatestPrice') return
      if (retryCount >= 10) return
      setTimeout(() => revalidate({ retryCount }), 420000)
    }
  }) || {}
  const { getLatestPrice: latestPrices } = data || {}
  const mergedData = React.useMemo(() => {
    if (!latestPrices) return heatmapData;

    const priceData = convertData(latestPrices);

    return heatmapData.map(item => {
      const tickerData = priceData[item.ticker];
      if (tickerData && tickerData.price_data.length >= 2) {
        const [latestPrice, previousPrice] = tickerData.price_data;
        item.PCHG__1 = getPercent(latestPrice.price, previousPrice.price);
        item.CHG__1 = roundNum(latestPrice.price - previousPrice.price);
        item.price = latestPrice.price;
        item.updatedAt = new Date()
      } else {
        item.PCHG__1 = null;
        item.CHG__1 = null;
      }
      return item;
    });
  }, [latestPrices]);

  return { data: mergedData, error, isValidating };
};

function formatDate(dateString, marketData) {
  try {
    const date = new Date(dateString[0] + dateString[1]);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date string format. Please use "MM/DD/YYYY HH:MM:SS".');
    }
  } catch (error) {
    console.error('Error parsing date:', error.message);
    return null;
  }

  try {
    if (!marketData || !Object.prototype.hasOwnProperty.call(marketData, 'location_code') ||
      !Object.prototype.hasOwnProperty.call(marketData, 'start_time') ||
      !Object.prototype.hasOwnProperty.call(marketData, 'end_time') ||
      !Object.prototype.hasOwnProperty.call(marketData, 'timezone')) {
      throw new Error('Missing or incomplete market data. Please provide all required fields.');
    }
  } catch (error) {
    console.error('Error with market data:', error.message);
    return null;
  }

  const date = new Date(dateString[0] + dateString[1]);
  const day = date.toLocaleDateString("en-US", { weekday: "short" });
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const dayOfMonth = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const now = new Date().toLocaleString("en-US", { timeZone: marketData.timezone });
  const nowNY = new Date(now);

  const startTime = new Date(date.toDateString() + " " + marketData.start_time);
  const endTime = new Date(date.toDateString() + " " + marketData.end_time);

  let marketStatus = "closed";
  let remainingHours = 0;
  if (nowNY >= startTime && nowNY <= endTime) {
    marketStatus = "open";
  } else if (nowNY < startTime) {
    const diff = startTime - nowNY;
    remainingHours = Math.floor(diff / (1000 * 60 * 60));
  }
  let additionalText = "";
  if (marketStatus === "closed") {
    if (remainingHours) {
      additionalText = `Markets closed and will open in ${remainingHours} hour${remainingHours > 1 ? 's' : ''}`;
    } else {
      additionalText = "Markets Closed";
    }
  } else {
    additionalText = "Markets Open";
  }

  return `${day}, ${month} ${dayOfMonth}, ${year}, ${time} ET - ${additionalText}`;
}

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

const LISTBOX_PADDING = 8;

function StockSearchResultRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <Box
        key={dataSet.key}
        component="div"
        style={inlineStyle}
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: colors.neutral[500],
          px: 2,
          display: 'flex',
          alignItems: 'flex-end',
          pb: 0.5,
          bgcolor: colors.neutral[0],
          borderBottom: `1px solid ${colors.neutral[100]}`,
        }}
      >
        {dataSet.group}
      </Box>
    );
  }

  return (
    <Box
      component="li"
      {...dataSet[0]}
      style={inlineStyle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1.5,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover, &.Mui-focused': {
          bgcolor: colors.neutral[50],
        },
        '&[aria-selected="true"]': {
          bgcolor: colors.accent[50],
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: colors.neutral[900],
          minWidth: 56,
          flexShrink: 0,
          textTransform: 'uppercase',
        }}
      >
        {dataSet[1].t}
      </Typography>
      <Typography
        component="span"
        noWrap
        title={dataSet[1]?.n1}
        sx={{
          fontSize: '0.8rem',
          color: colors.neutral[500],
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {dataSet[1].n1}
      </Typography>
    </Box>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 44 : 52;

  const getChildSize = (child) => {
    if (child.hasOwnProperty('group')) return 36;
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) return 8 * itemSize;
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {StockSearchResultRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const SearchPopper = styled(Popper)({
  [`& .${autocompleteClasses.paper}`]: {
    borderRadius: radii.md,
    boxShadow: shadows.lg,
    border: `1px solid ${colors.neutral[200]}`,
    overflow: 'hidden',
    marginTop: 8,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    padding: 0,
    '& ul': { padding: 0, margin: 0 },
  },
});

const StockPage = (props) => {
  const {
    home_tabs, stock_news: getNewsfeed, latest_filings, posts,
    pageLoading, hero, stockideas_ads_settings, heatmapData, heatmapSettings,
    highMultiCrushed, todaysWinnersnLosers, recentEarnings,
  } = props || {}
  const { getLatestFilings } = latest_filings || {}
  const auth = useAuth() || {};
  const router = useRouter()
  const { email, claims } = auth || {}
  const { userType } = claims || {}

  const [value, setValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [invalidTicker, setInvalidTicker] = React.useState(false);
  const loading = open && options.length === 0;

  React.useEffect(() => {
    let active = true;
    if (!loading) return;
    (async () => {
      const stockSearch_jsonData = await searchTicker({
        url: `/api/stock-search`,
        searchString: "",
      }) || [];
      if (active) setOptions([...stockSearch_jsonData]);
    })();
    return () => { active = false; };
  }, [loading]);

  React.useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  const { data, error, isValidating } = useSWR(["api/_getTickerInfo", 'AAPL'], _getTickerInfo,
    {
      refreshInterval: 180000,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404) return
        if (key === 'api/_getTickerInfo') return
        if (retryCount >= 10) return
        setTimeout(() => revalidate({ retryCount }), 180000)
      },
    }
  )

  const { data: mergedHeatMapData, error: error2, isValidating: loadingHeatMapData } = useMergedData(heatmapData);

  const { getTradingHours, getTickerInfo } = data && data?.getTickerInfo && data?.getTradingHours && !error ? data : {}
  const { realtime_stock_data, } = getTickerInfo || {}
  const currDateTime = new Date(getTradingItem({ recordDate: new Date() })?.recordDate);
  let lastEndTime = realtime_stock_data && realtime_stock_data.length > 0 ? new Date(getTradingItem({ recordDate: realtime_stock_data[0]?.record_date })?.recordDate) : null;
  let lastStartTime = realtime_stock_data && realtime_stock_data.length > 0 ? new Date(getTradingItem({ recordDate: realtime_stock_data[0]?.record_date })?.recordDate) : null;
  lastEndTime = lastEndTime && getTradingHours ? new Date(convertDate(lastEndTime) + 'T' + getTradingHours?.end_time) : null;
  lastStartTime = lastStartTime && getTradingHours && getTradingHours?.end_time ? new Date(convertDate(lastStartTime) + 'T' + getTradingHours?.start_time) : null;
  const isTrading = lastEndTime && lastStartTime && +lastEndTime > +currDateTime && +lastStartTime < +currDateTime;
  lastEndTime = isTrading && realtime_stock_data.length > 1 && realtime_stock_data[1]?.record_date ? new Date(`${convertDate(new Date(realtime_stock_data[1]?.record_date))} ${getTradingHours?.end_time}`) : lastEndTime;
  let NewYorkTime = new Date().toLocaleString('en-US', { timeZone: getTradingHours && getTradingHours?.timezone ? getTradingHours.timezone : 'America/New_York', })?.split(',') || null;

  const formattedDate = formatDate(NewYorkTime, getTradingHours);

  return (
    <Box component="div">
      {/* ── Hero / Search Section ── */}
      <Box
        sx={{
          bgcolor: colors.neutral[50],
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 50% 0%, ${colors.accent[50]}66 0%, transparent 65%)`,
            pointerEvents: 'none',
          }}
        />
        <TopMetricsWithMarquee
          marketStatus={formattedDate ?
            <Typography component="p" sx={{ mx: 3, fontSize: '.7rem', color: formattedDate ? '#595959' : colors.neutral[50] }}>
              {formattedDate}
            </Typography> : null}
        />
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 560,
              mx: 'auto',
              py: { xs: 5, md: 7 },
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
                {hero?.h1 ?? `Stock Market Insights`}
              </Typography>
              <Typography variant="subtitle1" component="p">
                {hero?.desc ?? `Start your search for the perfect stock below.`}
              </Typography>
            </Box>

            {/* ── Search Bar ── */}
            <Box
              component="form"
              onSubmit={(e) => e.preventDefault()}
              sx={{
                width: '100%',
                position: 'relative',
                zIndex: open ? (theme) => theme.zIndex.appBar - 1 : 0,
              }}
            >
              <Autocomplete
                id="search-tickers"
                autoHighlight
                disableListWrap
                PopperComponent={SearchPopper}
                ListboxComponent={ListboxComponent}
                sx={{ width: '100%' }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onChange={(event, newValue) => {
                  setValue(newValue);
                  if (router && newValue && newValue?.t) {
                    router.push(`/stock/${newValue?.t}`);
                  }
                  send.uevent(event, `stock-home-search ${newValue?.t}`);
                }}
                onInputChange={(event, newInputValue) => {
                  setValue(newInputValue);
                  const available = options.filter(item =>
                    item.n1.toLowerCase().startsWith(newInputValue.trim().toLowerCase()) ||
                    item.t.toLowerCase().startsWith(newInputValue.trim().toLowerCase())
                  );
                  setInvalidTicker(!(available.length > 0) && newInputValue.trim() !== '');
                }}
                isOptionEqualToValue={(option, value) => option.n1 === value.n1}
                filterOptions={(options, { inputValue }) =>
                  inputValue.trim() !== ""
                    ? options
                        .filter(item =>
                          item.n1.toLowerCase().startsWith(inputValue.trim().toLowerCase()) ||
                          item.t.toLowerCase().startsWith(inputValue.trim().toLowerCase())
                        )
                        .sort((a, b) => a.t.length - b.t.length)
                    : []
                }
                options={options}
                loading={loading}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return '';
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderOption={(props, option) => [props, option]}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for AAPL, TSLA, GOOGL..."
                    variant="outlined"
                    hiddenLabel
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                          {loading ? (
                            <CircularProgress size={20} thickness={2.5} sx={{ color: colors.accent.main }} />
                          ) : (
                            <SearchIcon sx={{ color: colors.neutral[400], fontSize: 22 }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 52,
                        borderRadius: `${radii.xl}px`,
                        bgcolor: colors.neutral[0],
                        fontSize: { xs: '16px', sm: '0.95rem' },
                        boxShadow: shadows.input,
                        transition: 'box-shadow 200ms ease, border-color 200ms ease',
                        '& fieldset': {
                          borderColor: colors.neutral[200],
                          transition: 'border-color 200ms ease',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.neutral[300],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.accent.main,
                          borderWidth: '1.5px',
                        },
                        '&.Mui-focused': {
                          boxShadow: shadows.inputFocus,
                        },
                        '&.Mui-focused .MuiInputAdornment-root svg': {
                          color: colors.accent.main,
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 0,
                        color: colors.neutral[900],
                        '&::placeholder': {
                          color: colors.neutral[400],
                          opacity: 1,
                        },
                      },
                      '& .MuiFilledInput-input': {
                        color: colors.neutral[900],
                      },
                    }}
                  />
                )}
                renderGroup={(params) => params}
              />
              {invalidTicker && (
                <Box
                  sx={{
                    mt: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: `${radii.sm}px`,
                    bgcolor: colors.neutral[0],
                    border: `1px solid ${colors.neutral[200]}`,
                    boxShadow: shadows.sm,
                  }}
                >
                  <Typography sx={{ color: colors.neutral[600], fontSize: '0.875rem' }}>
                    {`We don't cover this stock yet.`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Main Content ── */}
      <Container maxWidth="xl">
        <Box sx={{ width: '100%' }}>
          <Grid container
            spacing={{ xs: 2, md: 1.5 }}
            columns={12}
            direction="row-reverse"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid size={{ xs: 12, sm: 12, md: 7.8 }}>
              <StocksHeatMap
                data={mergedHeatMapData}
                isLoading={loadingHeatMapData}
                heatmapColorSettings={heatmapSettings ? heatmapSettings.settings : null}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4.2 }}>
              <TodaysWinnersLosers
                todaysWinnersnLosers={todaysWinnersnLosers}
              />
            </Grid>
          </Grid>
          <StockIdeasAdvertisement
            stock_ideas={posts}
            positionBottom={false}
            pageLoading={pageLoading}
            ad_settings={stockideas_ads_settings}
            tab="stock"
          />
          <NewStock
            stock_news={getNewsfeed}
            latestFilings={getLatestFilings}
            highMultiCrushed={highMultiCrushed}
            recentEarnings={recentEarnings}
          />
        </Box>
      </Container>
    </Box>
  );
}

export async function getServerSideProps({ preview = false, type = "stock", limit = 23 }) {
  const latest_filings = (await getLatestFilings("")) || [];
  const { getWideScreenerMetricsV3: heatmap_data } = (await getStocksHeatMapData("")) || {};
  const meta_tags = (await getPageMetaTags('/stock', false)) || {};
  const { getMetaTags } = meta_tags || {};
  const { getBlogs: stock_ideas } = (await getStockIdeas()) || {};
  const posts = stock_ideas?.map((blog) => {
    const { data: frontmatter } = matter(blog?.content);
    return { slug: blog?.urlsuffix, frontmatter };
  });
  const hero_data = (await getStockTabs("AAPL", "HERO")) || [];
  const { getStockTabs: hero } = hero_data || {};
  const { getStockTabs: _Ads } = (await getStockTabs("AAPL", "ADS", preview)) || [];
  let stock_ideas_ads = _Ads && _Ads?.length > 0 ? _Ads?.find(d => d?.section_code === "stock_ideas_reports") : {};
  stock_ideas_ads = stock_ideas_ads && stock_ideas_ads?.settings ? stock_ideas_ads?.settings : "";
  const { getStockTabs: _tableData } = (await getStockTabs("", "STOCKHOME", preview)) || [];
  let highMultiCrushed = _tableData && _tableData?.length > 0 ? _tableData?.find(d => d?.section_code === "tickerTables") : {};
  highMultiCrushed = highMultiCrushed && highMultiCrushed?.settings ? highMultiCrushed?.settings : "";
  let todaysWinnersnLosers = _tableData && _tableData?.length > 0 ? _tableData?.find(d => d?.section_code === "todays") : {};
  todaysWinnersnLosers = todaysWinnersnLosers && todaysWinnersnLosers?.settings ? todaysWinnersnLosers?.settings : "";
  let recentEarnings = _tableData && _tableData?.length > 0 ? _tableData?.find(d => d?.section_code === "recentEarnings") : {};
  recentEarnings = recentEarnings && recentEarnings?.settings ? recentEarnings?.settings : "";
  const heat_tab_data = (await getStockTabs("AAPL", "STOCKHOME", "COLR", null, null, null)) || [];
  const { getStockTabs: _ } = heat_tab_data || {};
  let heatmap_settings = _ && _.length > 0 ? _.find(d => d.section_code === "heatmap") : null;

  return {
    props: {
      latest_filings,
      posts: posts?.sort(sortByDate) ?? [],
      hero: getHeroContent(hero, "stock") || {},
      stockideas_ads_settings: stock_ideas_ads || {},
      heatmapData: heatmap_data || [],
      heatmapSettings: heatmap_settings || [],
      highMultiCrushed,
      todaysWinnersnLosers,
      recentEarnings,
      meta_tags: getMetaTags?.tags || null,
    },
  };
}

export default StockPage;
